// src/components/admin/CountryFlagsManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'flag-icons/css/flag-icons.min.css';

// Common countries list with their ISO codes (for quick selection)
const COMMON_COUNTRIES = [
    { name: 'United States', code: 'us' },
    { name: 'United Kingdom', code: 'gb' },
    { name: 'Canada', code: 'ca' },
    { name: 'Australia', code: 'au' },
    { name: 'India', code: 'in' },
    { name: 'Pakistan', code: 'pk' },
    { name: 'Jamaica', code: 'jm' },
    { name: 'Trinidad and Tobago', code: 'tt' },
    { name: 'Guyana', code: 'gy' },
    { name: 'Haiti', code: 'ht' },
    { name: 'Lebanon', code: 'lb' },
    { name: 'Brazil', code: 'br' },
    { name: 'Mexico', code: 'mx' },
    { name: 'Argentina', code: 'ar' },
    { name: 'Philippines', code: 'ph' },
    { name: 'China', code: 'cn' },
    { name: 'Japan', code: 'jp' },
    { name: 'South Korea', code: 'kr' },
    { name: 'Germany', code: 'de' },
    { name: 'France', code: 'fr' },
    { name: 'Italy', code: 'it' },
    { name: 'Spain', code: 'es' },
    { name: 'Portugal', code: 'pt' },
    { name: 'Netherlands', code: 'nl' },
    { name: 'Belgium', code: 'be' },
    { name: 'Switzerland', code: 'ch' },
    { name: 'Austria', code: 'at' },
    { name: 'Poland', code: 'pl' },
    { name: 'Sweden', code: 'se' },
    { name: 'Norway', code: 'no' },
    { name: 'Denmark', code: 'dk' },
    { name: 'Finland', code: 'fi' },
    { name: 'Ireland', code: 'ie' },
    { name: 'New Zealand', code: 'nz' },
    { name: 'Singapore', code: 'sg' },
    { name: 'Malaysia', code: 'my' },
    { name: 'Thailand', code: 'th' },
    { name: 'Indonesia', code: 'id' },
    { name: 'Vietnam', code: 'vn' },
    { name: 'Saudi Arabia', code: 'sa' },
    { name: 'United Arab Emirates', code: 'ae' },
    { name: 'Qatar', code: 'qa' },
    { name: 'Kuwait', code: 'kw' },
    { name: 'Turkey', code: 'tr' },
    { name: 'Israel', code: 'il' },
    { name: 'Russia', code: 'ru' },
    { name: 'Ukraine', code: 'ua' },
    { name: 'Greece', code: 'gr' },
    { name: 'Czech Republic', code: 'cz' },
    { name: 'Hungary', code: 'hu' },
    { name: 'Romania', code: 'ro' }
].sort((a, b) => a.name.localeCompare(b.name));

const CountryFlagsManagement = () => {
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingFlag, setEditingFlag] = useState(null);
    const [formData, setFormData] = useState({
        country_name: '',
        country_code: '',
        flag_type: 'international',
        display_order: 0,
        is_active: true
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [useQuickSelect, setUseQuickSelect] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState('');

    const API_BASE = process.env.REACT_APP_API_URL;

    if (!API_BASE) {
        console.error('❌ CRITICAL: REACT_APP_API_URL not configured in CountryFlagsManagement!');
    }

    useEffect(() => {
        fetchFlags();
    }, []);

    const fetchFlags = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/country-flags`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();

            if (data.success) {
                // Sort by display order and then by name
                const sorted = (data.data || []).sort((a, b) => {
                    if (a.display_order !== b.display_order) {
                        return a.display_order - b.display_order;
                    }
                    return a.country_name.localeCompare(b.country_name);
                });
                setFlags(sorted);
            } else {
                toast.error('Failed to load country flags');
            }
        } catch (error) {
            console.error('Error fetching flags:', error);
            toast.error('Failed to load country flags');
        } finally {
            setLoading(false);
        }
    };

    const handleQuickSelect = (e) => {
        const countryCode = e.target.value;
        setSelectedCountry(countryCode);

        if (countryCode) {
            const country = COMMON_COUNTRIES.find(c => c.code === countryCode);
            if (country) {
                setFormData({
                    ...formData,
                    country_name: country.name,
                    country_code: country.code
                });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('adminToken');
            const url = editingFlag
                ? `${API_BASE}/admin/country-flags/${editingFlag.id}`
                : `${API_BASE}/admin/country-flags`;

            const method = editingFlag ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    country_code: formData.country_code.toLowerCase()
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(editingFlag ? 'Country flag updated successfully' : 'Country flag added successfully');
                fetchFlags();
                resetForm();
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving flag:', error);
            toast.error('Failed to save country flag');
        }
    };

    const handleEdit = (flag) => {
        setEditingFlag(flag);
        setFormData({
            country_name: flag.country_name,
            country_code: flag.country_code,
            flag_type: flag.flag_type,
            display_order: flag.display_order,
            is_active: flag.is_active
        });
        setShowForm(true);
        setUseQuickSelect(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this country flag?')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/country-flags/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Country flag deleted successfully');
                fetchFlags();
            } else {
                toast.error(data.message || 'Failed to delete');
            }
        } catch (error) {
            console.error('Error deleting flag:', error);
            toast.error('Failed to delete country flag');
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/country-flags/${id}/toggle`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message);
                fetchFlags();
            } else {
                toast.error(data.message || 'Failed to toggle status');
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to toggle status');
        }
    };

    const resetForm = () => {
        setFormData({
            country_name: '',
            country_code: '',
            flag_type: 'international',
            display_order: 0,
            is_active: true
        });
        setEditingFlag(null);
        setShowForm(false);
        setUseQuickSelect(true);
        setSelectedCountry('');
    };

    const filteredFlags = flags.filter(flag =>
        flag.country_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flag.country_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="mb-1" style={{ color: '#6B8E23' }}>
                                        <i className="bi bi-flag-fill me-2"></i>
                                        Country Flags Management
                                    </h4>
                                    <p className="text-muted small mb-0">
                                        Manage international country flags displayed on the homepage
                                    </p>
                                </div>
                                <button
                                    className="btn btn-sm"
                                    style={{ backgroundColor: '#6B8E23', color: 'white' }}
                                    onClick={() => setShowForm(!showForm)}
                                >
                                    <i className={`bi bi-${showForm ? 'x-lg' : 'plus-lg'} me-2`}></i>
                                    {showForm ? 'Cancel' : 'Add New Country'}
                                </button>
                            </div>
                        </div>

                        <div className="card-body">
                            {/* Add/Edit Form */}
                            {showForm && (
                                <div className="card mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                                    <div className="card-body">
                                        <h5 className="mb-4" style={{ color: '#6B8E23' }}>
                                            {editingFlag ? 'Edit Country Flag' : 'Add New Country Flag'}
                                        </h5>
                                        <form onSubmit={handleSubmit}>
                                            <div className="row">
                                                {/* Quick Select Toggle */}
                                                {!editingFlag && (
                                                    <div className="col-12 mb-3">
                                                        <div className="form-check form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="useQuickSelect"
                                                                checked={useQuickSelect}
                                                                onChange={(e) => {
                                                                    setUseQuickSelect(e.target.checked);
                                                                    if (!e.target.checked) {
                                                                        setFormData({ ...formData, country_name: '', country_code: '' });
                                                                        setSelectedCountry('');
                                                                    }
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor="useQuickSelect">
                                                                Use Quick Country Selection
                                                            </label>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Quick Select Dropdown */}
                                                {useQuickSelect && !editingFlag && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Quick Select Country</label>
                                                        <select
                                                            className="form-select"
                                                            value={selectedCountry}
                                                            onChange={handleQuickSelect}
                                                        >
                                                            <option value="">-- Select a Country --</option>
                                                            {COMMON_COUNTRIES.map(country => (
                                                                <option key={country.code} value={country.code}>
                                                                    {country.name} ({country.code.toUpperCase()})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                {/* Manual Entry or Display */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Country Name *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.country_name}
                                                        onChange={(e) => setFormData({ ...formData, country_name: e.target.value })}
                                                        required
                                                        disabled={useQuickSelect && !editingFlag}
                                                        placeholder="e.g., United States"
                                                    />
                                                </div>

                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">
                                                        Country Code (ISO 3166-1 alpha-2) *
                                                        <a
                                                            href="https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="ms-2 text-muted small"
                                                        >
                                                            <i className="bi bi-info-circle"></i> View codes
                                                        </a>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control text-lowercase"
                                                        value={formData.country_code}
                                                        onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toLowerCase() })}
                                                        required
                                                        disabled={useQuickSelect && !editingFlag}
                                                        placeholder="e.g., us"
                                                        maxLength={2}
                                                        pattern="[a-z]{2}"
                                                        title="Must be 2 lowercase letters"
                                                    />
                                                    <div className="form-text">2 lowercase letters (e.g., us, gb, ca)</div>
                                                </div>

                                                {/* Preview Flag */}
                                                {formData.country_code && formData.country_code.length === 2 && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Flag Preview</label>
                                                        <div className="p-3 bg-white rounded border">
                                                            <span
                                                                className={`fi fi-${formData.country_code.toLowerCase()}`}
                                                                style={{ fontSize: '48px' }}
                                                            ></span>
                                                            <div className="mt-2 small text-muted">
                                                                {formData.country_name}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Flag Type</label>
                                                    <select
                                                        className="form-select"
                                                        value={formData.flag_type}
                                                        onChange={(e) => setFormData({ ...formData, flag_type: e.target.value })}
                                                        required
                                                    >
                                                        <option value="international">International</option>
                                                        <option value="african">African (Not recommended - use hardcoded list)</option>
                                                    </select>
                                                    <div className="form-text">Use "International" for countries outside Africa</div>
                                                </div>

                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Display Order</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.display_order}
                                                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                                        min="0"
                                                    />
                                                    <div className="form-text">Lower numbers appear first</div>
                                                </div>

                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Status</label>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="is_active"
                                                            checked={formData.is_active}
                                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                        />
                                                        <label className="form-check-label" htmlFor="is_active">
                                                            {formData.is_active ? 'Active (Visible on website)' : 'Inactive (Hidden)'}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button type="submit" className="btn" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                                                    <i className="bi bi-check-lg me-2"></i>
                                                    {editingFlag ? 'Update Country' : 'Add Country'}
                                                </button>
                                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                                    <i className="bi bi-x-lg me-2"></i>
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* Search Bar */}
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="input-group">
                                        <span className="input-group-text bg-white">
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by country name or code..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <span className="text-muted">
                                        Total: {filteredFlags.length} {filteredFlags.length === 1 ? 'country' : 'countries'}
                                    </span>
                                </div>
                            </div>

                            {/* Flags List */}
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredFlags.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-flag display-1 text-muted"></i>
                                    <p className="text-muted mt-3">
                                        {searchTerm ? 'No countries found matching your search' : 'No countries added yet. Add your first country!'}
                                    </p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '80px' }}>Flag</th>
                                                <th>Country Name</th>
                                                <th style={{ width: '100px' }}>Code</th>
                                                <th style={{ width: '120px' }}>Type</th>
                                                <th style={{ width: '100px' }}>Order</th>
                                                <th style={{ width: '100px' }}>Status</th>
                                                <th style={{ width: '200px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredFlags.map((flag) => (
                                                <tr key={flag.id}>
                                                    <td>
                                                        <span
                                                            className={`fi fi-${flag.country_code}`}
                                                            style={{ fontSize: '32px' }}
                                                        ></span>
                                                    </td>
                                                    <td className="fw-semibold">{flag.country_name}</td>
                                                    <td>
                                                        <span className="badge bg-secondary text-uppercase">
                                                            {flag.country_code}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${flag.flag_type === 'international' ? 'bg-primary' : 'bg-info'}`}>
                                                            {flag.flag_type}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">{flag.display_order}</td>
                                                    <td>
                                                        <span className={`badge ${flag.is_active ? 'bg-success' : 'bg-danger'}`}>
                                                            {flag.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="btn-group btn-group-sm" role="group">
                                                            <button
                                                                className="btn btn-outline-primary"
                                                                onClick={() => handleEdit(flag)}
                                                                title="Edit"
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-warning"
                                                                onClick={() => handleToggleStatus(flag.id)}
                                                                title={flag.is_active ? 'Deactivate' : 'Activate'}
                                                            >
                                                                <i className={`bi bi-${flag.is_active ? 'eye-slash' : 'eye'}`}></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-danger"
                                                                onClick={() => handleDelete(flag.id)}
                                                                title="Delete"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Information Alert */}
                            <div className="alert alert-info mt-4" role="alert">
                                <h6 className="alert-heading">
                                    <i className="bi bi-info-circle me-2"></i>
                                    About Country Flags Display
                                </h6>
                                <p className="mb-0 small">
                                    <strong>African flags</strong> are hardcoded in the frontend and will always display first in the carousel.
                                    This management system is for adding <strong>international countries</strong> whose citizens have joined ARCA as members.
                                    International flags will appear beneath the African flags on the homepage.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CountryFlagsManagement;
